<template>
  <div :class="classes">
    <div class="consumption-from__arrow">
      <ConsumptionArrow
        :type="arrowType"
        :angle="angle"
        ref="arrow"
        :disabled="arrowDisabled" />
    </div>
    <div data-cy="consumption-value" class="consumption-from__value" :class="supplierColorClass">
      <Wattage :value="animatedValue" />
    </div>
    <div class="consumption-from__title">{{ title }}</div>
  </div>
</template>

<script>
  import ConsumptionArrow from '@/components/icons/ConsumptionArrow.vue'
  import Wattage from '@/components/Wattage.vue'
  import TweenLite from 'gsap'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useAccountStore } from '@/stores/useAccountStore';
  import { useCubeStore } from '@/stores/useCubeStore';
  import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore'
  import { WATTAGE_TWEEN_DURATION } from '@/constants.ts'

  export default {
    name: 'consumption-from',
    components: {
      ConsumptionArrow,
      Wattage,
    },
    setup(){
      const cubeStore = useCubeStore()
      const currentReadsStore = useCurrentReadsStore()
      const { t } = useStrings()
      return{
        t,
        cubeStore,
        currentReadsStore,
      }
    },
    data() {
      // Use decent defaults for the angles.
      let angle = 35
      if (this.type === 'supplier') {
        angle *= -1
      }

      return {
        angle: angle,
        animatedValue: 0,
      }
    },
    props: {
      type: {
        type: String,
        required: true,
      },
    },
    mounted() {
      this.animatedValue = this.rawValue
    },
    computed: {
      classes: function () {
        const classes = [
          'consumption-from',
          `consumption-from--${this.type}`
        ]

        return classes.join(' ')
      },
      supplierColorClass: function() {
        const useHighContrast = useAccountStore().isHighContrast;
        if (this.type == "supplier" && useHighContrast) {
          return ["high-contrast"];
        } else {
          return [];
        }
      },
      arrowType: function () {
        let result = this.type === 'community' ? 'positive' : 'negative'

        if (this.type === 'supplier' && this.currentReadsStore.toSupplier > 0) {
          result = 'positive'
        }

        return result
      },
      arrowDisabled: function () {
        return this.rawValue < 0.1
      },
      title: function () {
        if (this.type === 'community') {
          return this.t('home.communityConsumption')
        } else {
          return this.cubeStore.supplier.name
        }
      },
      rawValue: function () {
        const reads = this.currentReadsStore
        if (this.type === 'community') {
          if (reads.fromCommunity > 0) {
            return reads.fromCommunity + reads.communityConsumable
          } else {
            return reads.toCommunity
          }
        } else {
          if (reads.fromSupplier > 0) {
            return reads.fromSupplier
          } else {
            return reads.toSupplier
          }
        }
      },
      dotCoordinates: function () {
        const c = this.currentReadsStore.dotCoordinates
        return { positiveX: c.positiveX, positiveY: c.positiveY, negativeX: c.negativeX, negativeY: c.negativeY }
      },
    },
    methods: {
      updateArrowAngle: function (dotCoordinates) {
        const arrowCenter = this.getArrowCenterCoordinates()
        let dotCenter = {
          x: 0,
          y: 0,
        }
        let deltaX = 0
        let deltaY = 0

        if (this.type === 'community') {
          dotCenter.x = dotCoordinates.positiveX
          dotCenter.y = dotCoordinates.positiveY
          deltaX = arrowCenter.x - dotCenter.x
          deltaY = arrowCenter.y - dotCenter.y
        } else {
          dotCenter.x = dotCoordinates.negativeX
          dotCenter.y = dotCoordinates.negativeY
          deltaX = dotCenter.x - arrowCenter.x
          deltaY = dotCenter.y - arrowCenter.y
        }

        // Calculating the angle of a right triangle.
        // The triangle consists between the 2 center points and the deltas
        // between their x and y coordinates.
        // The angle in radians.
        this.angle = Math.atan(deltaY / deltaX)
        // Convert to degrees correct sign and round.
        this.angle = Math.round((180 / Math.PI) * this.angle)

        const reads = this.currentReadsStore

        // Adapt the arrow angles considering this.type and meter values.
        if (this.type === 'community') {
          if (reads.toCommunity > 0) {
            this.angle -= 90
          } else {
            this.angle += 90
          }
        } else {
          if (reads.toSupplier > 0) {
            this.angle += 90
          } else {
            this.angle -= 90
          }
        }
      },
      getArrowCenterCoordinates() {
        const rect = this.$refs.arrow.$el.getBoundingClientRect()
        const top = document.body.scrollTop
        const left = document.body.scrollLeft

        const result = {
          x: Math.round(left + rect.left + rect.width / 2),
          y: Math.round(top + rect.top + rect.height / 2),
        }
        return result
      },
    },
    watch: {
      dotCoordinates: function (newCoordinates) {
        this.updateArrowAngle(newCoordinates)
      },
      rawValue: function (newValue) {
        this.updateArrowAngle(this.dotCoordinates)
        TweenLite.to(this.$data, WATTAGE_TWEEN_DURATION, {
          animatedValue: newValue,
        })
      },
    },
  }
</script>

<style lang="scss">
  .consumption-from {
    text-align: center;

    // The arrows would otherwise overflow due to them being scaled inline svgs.
    overflow: hidden;

    &__arrow {
      transition: $transition-base;
      margin-bottom: 1vh;

      @media (min-height: 600px) {
        margin-bottom: 0;
      }

      @media (min-height: 1000px) {
        margin-bottom: 1vh;
      }

      @media (min-height: 1000px), (min-width: 700px) {
        .consumption-from--community & {
          margin-left: $spacer30;
        }

        .consumption-from--supplier & {
          margin-right: $spacer30;
        }
      }
    }

    &__title {
      line-height: $line-height-min;
      margin-top: $spacer01;
      font-size: $font-size-sm;

      @media (min-width: $bp-xs) {
        font-size: $font-size-base;
      }
    }

    &__value {
      margin-top: $spacer01;

      // supplier
      color: $color-secondary;

      &.high-contrast {
        color: $color-secondary-high-contrast;
      }

      .consumption-from--community & {
        color: $community;
      }
    }
  }
</style>
